/*
 * Pure helpers for the inbox conversation view: group messages into day
 * sections and locate the first unread received message. No Preact / no store
 * dependencies so this is unit-testable in isolation.
 */

// Local-time YYYY-MM-DD bucket for a date, or 'unknown' for an invalid input.
export function dayKey(date) {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) {
        return 'unknown';
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

// Classifies a message date relative to `now` into 'today' | 'yesterday' |
// the raw day key | 'unknown'. Callers map these to localized labels.
export function dayLabelKey(messageDate, now = new Date()) {
    const key = dayKey(messageDate);
    if (key === 'unknown') {
        return 'unknown';
    }
    const todayKey = dayKey(now);
    const yest = new Date(now);
    yest.setDate(yest.getDate() - 1);
    const yesterdayKey = dayKey(yest);
    if (key === todayKey) {
        return 'today';
    }
    if (key === yesterdayKey) {
        return 'yesterday';
    }
    return key;
}

// Interleaves day dividers and a single "new messages" divider with messages.
// `messages` MUST be chronological (oldest→newest), as the store holds them.
// `currentUserUuid` marks "my" messages (never counted as unread).
export function buildMessageRenderList(messages, currentUserUuid, now = new Date()) {
    const items = [];
    if (!Array.isArray(messages) || messages.length === 0) {
        return items;
    }
    const firstUnreadIdx = messages.findIndex(m => !m.readDate && m.fromUser !== currentUserUuid);
    let lastKey = null;
    messages.forEach((message, idx) => {
        const when = message.sentDate || message.createdDate;
        const key = dayKey(when);
        if (key !== lastKey) {
            items.push({ type: 'day', labelKey: dayLabelKey(when, now) });
            lastKey = key;
        }
        if (idx === firstUnreadIdx) {
            items.push({ type: 'newMessages' });
        }
        items.push({ type: 'message', message });
    });
    return items;
}
